import { keepPreviousData, queryOptions, type QueryKey } from '@tanstack/react-query';

import { ApiError } from '@/shared/http';
import { reportError } from '@/shared/telemetry';

/**
 * Erro de código ao montar o dado (não de rede nem da API): vai ao console também em produção, só com
 * nome, mensagem e pilha, e à telemetria. Sem isso a tela mostra só "unknown" e ninguém sabe o porquê.
 */
function reportDataCrash(queryKey: QueryKey, error: unknown) {
  const scope = typeof queryKey[0] === 'string' ? queryKey[0] : 'query';
  if (error instanceof Error) console.error(`${scope}: o dado quebrou ao ser montado: ${error.name}: ${error.message}`, error.stack ?? '');
  else console.error(`${scope}: o dado quebrou ao ser montado (${typeof error})`);
  reportError(error, { 'error.source': 'query.fetcher', 'uyzy.query': scope });
}

type Fetcher<T> = (context: { signal: AbortSignal }) => Promise<T>;

interface Extra<T> {
  staleTime?: number;
  enabled?: boolean;
  select?: (data: T) => T;
}

/** `queryOptions` para detalhe: repassa o `signal` para o axios cancelar e garante `ApiError`. */
export function apiQuery<T, K extends QueryKey>(queryKey: K, fetcher: Fetcher<T>, extra: Extra<T> = {}) {
  return queryOptions<T, ApiError, T, K>({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        return await fetcher({ signal });
      } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError.kind === 'unknown' && !(error instanceof ApiError)) reportDataCrash(queryKey, error);
        throw apiError;
      }
    },
    ...extra,
  });
}

/** Igual a `apiQuery`, mas mantém a página anterior na tela enquanto a próxima carrega. */
export function apiListQuery<T, K extends QueryKey>(queryKey: K, fetcher: Fetcher<T>, extra: Extra<T> = {}) {
  return queryOptions<T, ApiError, T, K>({
    ...apiQuery(queryKey, fetcher, extra),
    placeholderData: keepPreviousData,
  });
}
